import React, { useEffect, useState } from "react";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CopyToClipboard from "react-copy-to-clipboard";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy } from "@fortawesome/free-solid-svg-icons";
import clauseData from "../../data/Ontario/clauseData.js";
import OnCallSchedule from "../../components/Ontario/OnCallSchedule.js";
import ManagingBrokerContacts from "../../components/Ontario/ManagingBrokerContacts.js";
import "../../styles/OfficeProcedures.css";
import "../../styles/Clause.css";

const novateClauseEntry = clauseData.find(
  (c) => c.type === "clause" && c.title?.includes("Novate Clause"),
);
const NOVATE_CLAUSE_COPY_TEXT =
  novateClauseEntry?.text.replace(/(<|&lt;)br\s*\/*(>|&gt;)/g, "") ?? "";

export default function OfficeProcedures({ setProvince }) {
  const theme = createTheme({
    palette: {
      primary: {
        main: "#506CAA",
        dark: "#506CAA",
      },
      text: {
        primary: "#31303F",
        secondary: "#506CAA",
      },
    },
  });

  const [expandedAccordion, setExpandedAccordion] = useState(false);
  const [novateCopyLabel, setNovateCopyLabel] = useState("Copy Clause");
  const [novateCopyColor, setNovateCopyColor] = useState("#1d3a8a");

  // Add CSS for accordion content
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      .accordion-content ol {
        margin-left: -15px !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  useEffect(() => {
    setProvince("ON");
  });

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedAccordion(isExpanded ? panel : false);

    // Scroll to the top of the first accordion when expanded (mobile only)
    if (isExpanded && window.innerWidth <= 768) {
      console.log(
        "Accordion clicked, panel:",
        panel,
        "isExpanded:",
        isExpanded,
      );

      // Use a longer timeout for iOS and add multiple attempts
      const scrollToFirstAccordion = () => {
        // Try multiple selectors to find the first accordion
        const firstAccordion =
          document.querySelector('[data-testid="accordion-0"]') ||
          document.querySelector(".MuiAccordion-root") ||
          document.querySelector('[role="region"]');
        console.log("Found first accordion:", firstAccordion);

        if (firstAccordion) {
          console.log("Attempting to scroll to first accordion");
          try {
            // Get the position and add offset to position just below header
            const rect = firstAccordion.getBoundingClientRect();
            const scrollTop =
              window.pageYOffset || document.documentElement.scrollTop;
            const targetPosition = scrollTop + rect.top - 80; // 80px offset to position below header

            console.log("Current scroll position:", scrollTop);
            console.log("Accordion rect.top:", rect.top);
            console.log("Target position:", targetPosition);

            // Use requestAnimationFrame for smoother scrolling on iOS
            requestAnimationFrame(() => {
              window.scrollTo({
                top: targetPosition,
                behavior: "smooth",
              });
            });
          } catch (error) {
            console.error("Error scrolling to first accordion:", error);
          }
        } else {
          console.log("No first accordion found, trying fallback");
          // Fallback: try to scroll to the transaction topic container
          const transactionContainer = document.querySelector(".max-w-5xl");
          if (transactionContainer) {
            console.log("Using fallback container");
            const rect = transactionContainer.getBoundingClientRect();
            const scrollTop =
              window.pageYOffset || document.documentElement.scrollTop;
            const targetPosition = scrollTop + rect.top - 80;

            requestAnimationFrame(() => {
              window.scrollTo({
                top: targetPosition,
                behavior: "smooth",
              });
            });
          }
        }
      };

      // First attempt after a longer delay for iOS
      setTimeout(scrollToFirstAccordion, 100);

      // Second attempt after a longer delay for slower devices
      setTimeout(scrollToFirstAccordion, 300);

      // Third attempt after an even longer delay for very slow devices
      setTimeout(scrollToFirstAccordion, 500);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <div className="body pt-10">
        <div className="max-w-5xl w-full flex justify-left mt-5">
          <div className="mt-2 mb-1 max-w-5xl w-full">
            <Accordion
              expanded={expandedAccordion === "panel8"}
              onChange={handleAccordionChange("panel8")}
              sx={{
                mb: 1,
                borderRadius: "5px 5px 0 0",
                backgroundColor: "#ffffff",
                border: "1px solid #e0e0e0",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                "&:hover": {
                  boxShadow: "0 4px 8px rgba(0,0,0,0.15)",
                },
                "& .MuiAccordionDetails-root": {
                  padding: "24px 15px 15px 15px",
                },
                "&.Mui-expanded": {
                  margin: "8px 0",
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: "#0c0f24" }} />}
                aria-controls="panel8-content"
                id="panel8-header"
                sx={{
                  backgroundColor: "#ffffff",
                  borderBottom: "1px solid #e0e0e0",
                  borderTopLeftRadius: "5px",
                  borderTopRightRadius: "5px",
                  minHeight: "48px",
                  height: "48px",
                  "@media (max-width: 768px)": {
                    minHeight: "60px",
                    height: "60px",
                  },
                  "& .MuiAccordionSummary-content": {
                    margin: "8px 0",
                  },
                  "& .MuiAccordionSummary-expandIconWrapper": {
                    transform: "rotate(0deg)",
                    transition: "transform 0.2s ease-in-out",
                  },
                  "&.Mui-expanded .MuiAccordionSummary-expandIconWrapper": {
                    transform: "rotate(180deg)",
                  },
                  "&:hover": {
                    backgroundColor: "rgba(12, 15, 36, 0.1)",
                  },
                  "&.Mui-expanded": {
                    backgroundColor: "rgba(12, 15, 36, 0.1)",
                    minHeight: "48px",
                    height: "48px",
                    "@media (max-width: 768px)": {
                      minHeight: "60px",
                      height: "60px",
                    },
                  },
                }}
              >
                <div
                  style={{
                    fontWeight: "600",
                    color: "#0c0f24",
                    fontSize: "16px",
                    fontFamily: "Manrope, sans-serif",
                  }}
                >
                  Managing Broker Contact Information & Weekend On Call
                </div>
              </AccordionSummary>
              <AccordionDetails className="accordion-content">
                <OnCallSchedule isExpanded={expandedAccordion === "panel8"} />
                {expandedAccordion === "panel8" ? (
                  <ManagingBrokerContacts />
                ) : null}
              </AccordionDetails>
            </Accordion>

            <Accordion
              expanded={expandedAccordion === "panel1"}
              onChange={handleAccordionChange("panel1")}
              sx={{
                mb: 1,
                borderRadius: "5px 5px 0 0",
                backgroundColor: "#ffffff",
                border: "1px solid #e0e0e0",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                "&:hover": {
                  boxShadow: "0 4px 8px rgba(0,0,0,0.15)",
                },
                "& .MuiAccordionDetails-root": {
                  padding: "24px 15px 15px 15px",
                },
                "&.Mui-expanded": {
                  margin: "8px 0",
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: "#0c0f24" }} />}
                aria-controls="panel1-content"
                id="panel1-header"
                sx={{
                  backgroundColor: "#ffffff",
                  borderBottom: "1px solid #e0e0e0",
                  borderTopLeftRadius: "5px",
                  borderTopRightRadius: "5px",
                  minHeight: "48px",
                  height: "48px",
                  "@media (max-width: 768px)": {
                    minHeight: "60px",
                    height: "60px",
                  },
                  "& .MuiAccordionSummary-content": {
                    margin: "8px 0",
                  },
                  "& .MuiAccordionSummary-expandIconWrapper": {
                    transform: "rotate(0deg)",
                    transition: "transform 0.2s ease-in-out",
                  },
                  "&.Mui-expanded .MuiAccordionSummary-expandIconWrapper": {
                    transform: "rotate(180deg)",
                  },
                  "&:hover": {
                    backgroundColor: "rgba(12, 15, 36, 0.1)",
                  },
                  "&.Mui-expanded": {
                    backgroundColor: "rgba(12, 15, 36, 0.1)",
                    minHeight: "48px",
                    height: "48px",
                    "@media (max-width: 768px)": {
                      minHeight: "60px",
                      height: "60px",
                    },
                  },
                }}
              >
                <div
                  style={{
                    fontWeight: "600",
                    color: "#0c0f24",
                    fontSize: "16px",
                    fontFamily: "Manrope, sans-serif",
                  }}
                >
                  Deposit Instructions & Schedule B1
                </div>
              </AccordionSummary>
              <AccordionDetails className="accordion-content">
                <p>
                  Please complete the form, as linked below, for all deals so
                  that you are sending the most up to date deposit delivery
                  instructions to the other agent. For any deposit issues please
                  connect with the Deposits Team:{" "}
                  <a href="mailto:ondeposits@exprealty.net">
                    ondeposits@exprealty.net
                  </a>{" "}
                  or 1-833-497-4422.
                  <br />
                  <br />
                  <a
                    href="https://exprealty.formstack.com/forms/ontario_deposit"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Deposit Instructions Form
                  </a>
                </p>
                <br />
                <p>
                  All new listings & transactions where eXp is holding the
                  deposit will require the mandatory, company-provided Schedule
                  B1.
                </p>
                <p>
                  <br />
                  <a
                    href="https://drive.google.com/drive/folders/1X_IjZH_EskRCi0XVARdtmbydB6f6ZQkl"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Click here to get our Schedule B1
                  </a>
                </p>
              </AccordionDetails>
            </Accordion>

            <Accordion
              expanded={expandedAccordion === "panel2"}
              onChange={handleAccordionChange("panel2")}
              sx={{
                mb: 1,
                borderRadius: "5px 5px 0 0",
                backgroundColor: "#ffffff",
                border: "1px solid #e0e0e0",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                "&:hover": {
                  boxShadow: "0 4px 8px rgba(0,0,0,0.15)",
                },
                "& .MuiAccordionDetails-root": {
                  padding: "24px 15px 15px 15px",
                },
                "&.Mui-expanded": {
                  margin: "8px 0",
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: "#0c0f24" }} />}
                aria-controls="panel2-content"
                id="panel2-header"
                sx={{
                  backgroundColor: "#ffffff",
                  borderBottom: "1px solid #e0e0e0",
                  borderTopLeftRadius: "5px",
                  borderTopRightRadius: "5px",
                  minHeight: "48px",
                  height: "48px",
                  "@media (max-width: 768px)": {
                    minHeight: "60px",
                    height: "60px",
                  },
                  "& .MuiAccordionSummary-content": {
                    margin: "8px 0",
                  },
                  "& .MuiAccordionSummary-expandIconWrapper": {
                    transform: "rotate(0deg)",
                    transition: "transform 0.2s ease-in-out",
                  },
                  "&.Mui-expanded .MuiAccordionSummary-expandIconWrapper": {
                    transform: "rotate(180deg)",
                  },
                  "&:hover": {
                    backgroundColor: "rgba(12, 15, 36, 0.1)",
                  },
                  "&.Mui-expanded": {
                    backgroundColor: "rgba(12, 15, 36, 0.1)",
                    minHeight: "48px",
                    height: "48px",
                    "@media (max-width: 768px)": {
                      minHeight: "60px",
                      height: "60px",
                    },
                  },
                }}
              >
                <div
                  style={{
                    fontWeight: "600",
                    color: "#0c0f24",
                    fontSize: "16px",
                    fontFamily: "Manrope, sans-serif",
                  }}
                >
                  Lease Closing Procedure
                </div>
              </AccordionSummary>
              <AccordionDetails className="accordion-content">
                <p>
                  Since lawyers are typically not involved with lease closings,
                  eXp will need to be informed when the lease closes. <br />
                  <br />
                  Please email{" "}
                  <a href="mailto:onpayouts@exprealty.net">
                    onpayouts@exprealty.net
                  </a>{" "}
                  once the tenants have taken possession. This will trigger the
                  closing process so that commissions/excess funds can be
                  disbursed.
                  <br />
                </p>
              </AccordionDetails>
            </Accordion>

            <Accordion
              expanded={expandedAccordion === "panel3"}
              onChange={handleAccordionChange("panel3")}
              sx={{
                mb: 1,
                borderRadius: "5px 5px 0 0",
                backgroundColor: "#ffffff",
                border: "1px solid #e0e0e0",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                "&:hover": {
                  boxShadow: "0 4px 8px rgba(0,0,0,0.15)",
                },
                "& .MuiAccordionDetails-root": {
                  padding: "24px 15px 15px 15px",
                },
                "&.Mui-expanded": {
                  margin: "8px 0",
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: "#0c0f24" }} />}
                aria-controls="panel3-content"
                id="panel3-header"
                sx={{
                  backgroundColor: "#ffffff",
                  borderBottom: "1px solid #e0e0e0",
                  borderTopLeftRadius: "5px",
                  borderTopRightRadius: "5px",
                  minHeight: "48px",
                  height: "48px",
                  "@media (max-width: 768px)": {
                    minHeight: "60px",
                    height: "60px",
                  },
                  "& .MuiAccordionSummary-content": {
                    margin: "8px 0",
                  },
                  "& .MuiAccordionSummary-expandIconWrapper": {
                    transform: "rotate(0deg)",
                    transition: "transform 0.2s ease-in-out",
                  },
                  "&.Mui-expanded .MuiAccordionSummary-expandIconWrapper": {
                    transform: "rotate(180deg)",
                  },
                  "&:hover": {
                    backgroundColor: "rgba(12, 15, 36, 0.1)",
                  },
                  "&.Mui-expanded": {
                    backgroundColor: "rgba(12, 15, 36, 0.1)",
                    minHeight: "48px",
                    height: "48px",
                    "@media (max-width: 768px)": {
                      minHeight: "60px",
                      height: "60px",
                    },
                  },
                }}
              >
                <div
                  style={{
                    fontWeight: "600",
                    color: "#0c0f24",
                    fontSize: "16px",
                    fontFamily: "Manrope, sans-serif",
                  }}
                >
                  Documents Requiring Broker Signature (Cancellations/Releases)
                </div>
              </AccordionSummary>
              <AccordionDetails className="accordion-content">
                <div className="mb-card">
                  <h2>Listing Cancellations/Suspensions </h2>
                  <ol>
                    <li>
                      Once the Seller signs the cancellation, upload the
                      document to your deal in SkySlope.
                    </li>
                    <li>
                      Send an email to{" "}
                      <a href="mailto:onlistings@exprealty.net">
                        onlistings@exprealty.net
                      </a>{" "}
                      to let them know a signature is required.
                    </li>
                  </ol>
                  <p>
                    <br />
                    <strong>Weekends & Holidays: </strong> Send to{" "}
                    <a href="mailto:on.broker@exprealty.net">
                      on.broker@exprealty.net
                    </a>{" "}
                  </p>
                  <p className="pt-5">
                    All documents are processed by admin during regular office
                    hours: Monday to Friday: 9am - 5pm
                  </p>
                </div>
                <br />
                <div className="mb-card">
                  <h2>Mutual Releases</h2>
                  <p>
                    <strong>Please Note:</strong> There is no urgency to have a
                    Managing Broker sign a mutual release. Once buyers/sellers
                    have signed the document, the deal between those parties is
                    considered released.
                  </p>
                  <br />
                  <ol>
                    <li>Complete FINTRAC documents.</li>
                    <li>
                      Once Buyers & Sellers have signed, upload to SkySlope.
                    </li>
                    <li>
                      Send an email to{" "}
                      <a href="mailto:ontransactions@exprealty.net">
                        ontransactions@exprealty.net
                      </a>{" "}
                      to let them know the Mutual Release is in your file - this
                      way we can ensure a speedy process when returning funds in
                      trust.
                    </li>

                    <li>
                      If eXp is holding the deposit, collect a void cheque or
                      bank deposit form from the Buyer's agent so that funds can
                      be returned without delay.
                    </li>
                  </ol>
                  <br />
                  <p>
                    <strong>Weekends & Holidays: </strong> Send to{" "}
                    <a href="mailto:on.broker@exprealty.net">
                      on.broker@exprealty.net
                    </a>{" "}
                  </p>
                  <br />
                  <p>
                    Ideally, the order of signing should be the buyer, seller,
                    buyer's brokerage and the listing brokerage is typically
                    last.
                  </p>
                  <p className="pt-5">
                    All documents are processed by admin during regular office
                    hours: Monday to Friday: 9am - 5pm
                  </p>
                </div>
                <br />
                <div>
                  <h2>Registrant Disclosures, BRA/TRA Cancellations...etc. </h2>
                  <ol>
                    <li>
                      Upload the document into your preferred signing platform
                      and send the form to brokers for signature via{" "}
                      <a href="mailto:on.broker@exprealty.net">
                        on.broker@exprealty.net
                      </a>{" "}
                      (Signer's name should be set to "Managing Broker")
                    </li>
                  </ol>
                  <br />
                  <p>
                    <strong>Please Note:</strong> There is no urgency to have a
                    Managing Broker sign disclosure documents. The important
                    aspect is ensuring the parties being disclosed to sign
                    before an offer is presented.{" "}
                  </p>
                </div>
              </AccordionDetails>
            </Accordion>

            <Accordion
              expanded={expandedAccordion === "panel6"}
              onChange={handleAccordionChange("panel6")}
              sx={{
                mb: 1,
                borderRadius: "5px 5px 0 0",
                backgroundColor: "#ffffff",
                border: "1px solid #e0e0e0",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                "&:hover": {
                  boxShadow: "0 4px 8px rgba(0,0,0,0.15)",
                },
                "& .MuiAccordionDetails-root": {
                  padding: "24px 15px 15px 15px",
                },
                "&.Mui-expanded": {
                  margin: "8px 0",
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: "#0c0f24" }} />}
                aria-controls="panel6-content"
                id="panel6-header"
                sx={{
                  backgroundColor: "#ffffff",
                  borderBottom: "1px solid #e0e0e0",
                  borderTopLeftRadius: "5px",
                  borderTopRightRadius: "5px",
                  minHeight: "48px",
                  height: "48px",
                  "@media (max-width: 768px)": {
                    minHeight: "60px",
                    height: "60px",
                  },
                  "& .MuiAccordionSummary-content": {
                    margin: "8px 0",
                  },
                  "& .MuiAccordionSummary-expandIconWrapper": {
                    transform: "rotate(0deg)",
                    transition: "transform 0.2s ease-in-out",
                  },
                  "&.Mui-expanded .MuiAccordionSummary-expandIconWrapper": {
                    transform: "rotate(180deg)",
                  },
                  "&:hover": {
                    backgroundColor: "rgba(12, 15, 36, 0.1)",
                  },
                  "&.Mui-expanded": {
                    backgroundColor: "rgba(12, 15, 36, 0.1)",
                    minHeight: "48px",
                    height: "48px",
                    "@media (max-width: 768px)": {
                      minHeight: "60px",
                      height: "60px",
                    },
                  },
                }}
              >
                <div
                  style={{
                    fontWeight: "600",
                    color: "#0c0f24",
                    fontSize: "16px",
                    fontFamily: "Manrope, sans-serif",
                  }}
                >
                  Referral Agreements
                </div>
              </AccordionSummary>
              <AccordionDetails className="accordion-content">
                <div>
                  <p>
                    For any referrals within the province of Ontario, use OREA
                    FORM 641. For any referrals to another province or
                    international, see below.
                    <br />
                    <br />
                    <a
                      href="https://drive.google.com/drive/folders/1bWVrOnmhzI3ELm7mjUfYB9ct1GEanQuW"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Click here
                    </a>{" "}
                    to access the following Referral Agreements:
                    <br />
                    <br />
                    <ol>
                      <li>
                        Referral Agreement - Referral within Ontario or to
                        Another Province
                      </li>
                      <li>Referral Agreement - International Referral</li>
                    </ol>
                  </p>
                </div>
              </AccordionDetails>
            </Accordion>

            <Accordion
              expanded={expandedAccordion === "panel7"}
              onChange={handleAccordionChange("panel7")}
              sx={{
                mb: 1,
                borderRadius: "5px 5px 0 0",
                backgroundColor: "#ffffff",
                border: "1px solid #e0e0e0",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                "&:hover": {
                  boxShadow: "0 4px 8px rgba(0,0,0,0.15)",
                },
                "& .MuiAccordionDetails-root": {
                  padding: "24px 15px 15px 15px",
                },
                "&.Mui-expanded": {
                  margin: "8px 0",
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: "#0c0f24" }} />}
                aria-controls="panel7-content"
                id="panel7-header"
                sx={{
                  backgroundColor: "#ffffff",
                  borderBottom: "1px solid #e0e0e0",
                  borderTopLeftRadius: "5px",
                  borderTopRightRadius: "5px",
                  minHeight: "48px",
                  height: "48px",
                  "@media (max-width: 768px)": {
                    minHeight: "60px",
                    height: "60px",
                  },
                  "& .MuiAccordionSummary-content": {
                    margin: "8px 0",
                  },
                  "& .MuiAccordionSummary-expandIconWrapper": {
                    transform: "rotate(0deg)",
                    transition: "transform 0.2s ease-in-out",
                  },
                  "&.Mui-expanded .MuiAccordionSummary-expandIconWrapper": {
                    transform: "rotate(180deg)",
                  },
                  "&:hover": {
                    backgroundColor: "rgba(12, 15, 36, 0.1)",
                  },
                  "&.Mui-expanded": {
                    backgroundColor: "rgba(12, 15, 36, 0.1)",
                    minHeight: "48px",
                    height: "48px",
                    "@media (max-width: 768px)": {
                      minHeight: "60px",
                      height: "60px",
                    },
                  },
                }}
              >
                <div
                  style={{
                    fontWeight: "600",
                    color: "#0c0f24",
                    fontSize: "16px",
                    fontFamily: "Manrope, sans-serif",
                  }}
                >
                  Novate Clause (Revive a dead deal - requires Lawyer approval)
                </div>
              </AccordionSummary>
              <AccordionDetails className="accordion-content">
                <div>
                  <p>
                    When you have documents signed after the irrevocable or
                    conditions waived late, you can:
                    <ol>
                      <li>
                        Treat it as a dead deal and do entirely new paperwork
                      </li>
                      <li>
                        Execute an amendment with the novate clause below, with
                        written approval by the client's lawyer
                      </li>
                    </ol>
                    <br />
                    <strong>Clause:</strong>
                    <br />
                    Whereas the agreement dated XXX (the « Purchase Agreement »)
                    was terminated due to (the conditions not being waived in a
                    timely manner or due to acceptance being completed after the
                    irrevocable date). Whereas the parties have agreed to novate
                    and revive the Purchase Agreement in accordance with the
                    terms of this amendment – The parties agree as follows:{" "}
                    <br />
                    <br />
                    The Purchase Agreement is hereby restated and novated on the
                    same terms and conditions, subject to the following changes:
                    <br />
                    <br />
                    All deposits paid to date shall be credited to the purchase
                    price.
                    <br />
                    <br />
                    All conditions in the Purchase Agreement are deemed to have
                    been fulfilled or waived. (if required)
                    <br />
                    <br />
                    The new closing date shall be XXX. (if required)
                    <br />
                    <br />
                    All other terms and conditions of the Purchase Agreement
                    shall continue in full force and effect, and time shall
                    continue to be of the essence.
                    <br />
                    <br />
                  </p>
                  {NOVATE_CLAUSE_COPY_TEXT ? (
                    <CopyToClipboard
                      text={NOVATE_CLAUSE_COPY_TEXT}
                      onCopy={() => {
                        setNovateCopyColor("#00ad43");
                        setNovateCopyLabel("Copied");
                        window.setTimeout(() => {
                          setNovateCopyColor("#1d3a8a");
                          setNovateCopyLabel("Copy Clause");
                        }, 2500);
                      }}
                    >
                      <div>
                        <button
                          type="button"
                          className="copy-clause"
                          style={{ color: novateCopyColor }}
                        >
                          <FontAwesomeIcon
                            icon={faCopy}
                            size="lg"
                            className="copy-icon mr-1"
                          />
                          {novateCopyLabel}
                        </button>
                      </div>
                    </CopyToClipboard>
                  ) : null}
                </div>
              </AccordionDetails>
            </Accordion>

            <Accordion
              expanded={expandedAccordion === "panel4"}
              onChange={handleAccordionChange("panel4")}
              sx={{
                mb: 1,
                borderRadius: "5px 5px 0 0",
                backgroundColor: "#ffffff",
                border: "1px solid #e0e0e0",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                "&:hover": {
                  boxShadow: "0 4px 8px rgba(0,0,0,0.15)",
                },
                "& .MuiAccordionDetails-root": {
                  padding: "24px 15px 15px 15px",
                },
                "&.Mui-expanded": {
                  margin: "8px 0",
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: "#0c0f24" }} />}
                aria-controls="panel4-content"
                id="panel4-header"
                sx={{
                  backgroundColor: "#ffffff",
                  borderBottom: "1px solid #e0e0e0",
                  borderTopLeftRadius: "5px",
                  borderTopRightRadius: "5px",
                  minHeight: "48px",
                  height: "48px",
                  "@media (max-width: 768px)": {
                    minHeight: "60px",
                    height: "60px",
                  },
                  "& .MuiAccordionSummary-content": {
                    margin: "8px 0",
                  },
                  "& .MuiAccordionSummary-expandIconWrapper": {
                    transform: "rotate(0deg)",
                    transition: "transform 0.2s ease-in-out",
                  },
                  "&.Mui-expanded .MuiAccordionSummary-expandIconWrapper": {
                    transform: "rotate(180deg)",
                  },
                  "&:hover": {
                    backgroundColor: "rgba(12, 15, 36, 0.1)",
                  },
                  "&.Mui-expanded": {
                    backgroundColor: "rgba(12, 15, 36, 0.1)",
                    minHeight: "48px",
                    height: "48px",
                    "@media (max-width: 768px)": {
                      minHeight: "60px",
                      height: "60px",
                    },
                  },
                }}
              >
                <div
                  style={{
                    fontWeight: "600",
                    color: "#0c0f24",
                    fontSize: "16px",
                    fontFamily: "Manrope, sans-serif",
                  }}
                >
                  eXp SkySlope - Submitting your Documents to the Brokerage
                </div>
              </AccordionSummary>
              <AccordionDetails className="accordion-content">
                <p>
                  Deals should be submitted to the Brokerage via SkySlope within
                  48 hours of having an accepted agreement. If your board
                  provides access to SkySlope, as part of your board fees, this
                  is a different SkySlope than eXp's. Login to{" "}
                  <a
                    href="https://app.skyslope.com/LoginIntegrated.aspx"
                    target="_blank"
                    rel="noreferrer"
                  >
                    SkySlope
                  </a>{" "}
                  using your @exprealty.com email address.
                  <br />
                  <br />
                  Here are some training videos (2-3 mins) on how to create your
                  listing & transaction files:
                  <br />
                  <br />
                  <a
                    href="https://support.skyslope.com/support/solutions/articles/156000366460-create-a-transaction-canada-"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Create A Buyer/Tenant Transaction (3 mins)
                  </a>
                  <br /> <br />
                  <a
                    href="https://support.skyslope.com/support/solutions/articles/156000366461-create-a-listing"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Create A Listing Transaction (2.5 mins)
                  </a>
                  <br />
                  *Some features like transferring over docs automatically may
                  not be available.
                  <br /> <br />
                  <a
                    href="https://support.skyslope.com/support/solutions/articles/156000366436-convert-a-listing-to-a-transaction"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Convert a Listing to a Transaction: Accepted Offer on Your
                    Listing (3 min article)
                  </a>
                  <br />
                  <br />
                  <a
                    href="https://support.skyslope.com/support/solutions/articles/156000366393-using-split-assign"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Split & Assign Documents to the Checklist (2 mins)
                  </a>
                  <br />
                  <br />
                  <a
                    href="https://support.skyslope.com/support/solutions/articles/156000366392-attaching-a-document-to-the-checklist"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Attaching a Document to the Checklist (1 min)
                  </a>
                </p>
              </AccordionDetails>
            </Accordion>

            <Accordion
              expanded={expandedAccordion === "panel5"}
              onChange={handleAccordionChange("panel5")}
              sx={{
                mb: 1,
                borderRadius: "5px 5px 0 0",
                backgroundColor: "#ffffff",
                border: "1px solid #e0e0e0",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                "&:hover": {
                  boxShadow: "0 4px 8px rgba(0,0,0,0.15)",
                },
                "& .MuiAccordionDetails-root": {
                  padding: "24px 15px 15px 15px",
                },
                "&.Mui-expanded": {
                  margin: "8px 0",
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: "#0c0f24" }} />}
                aria-controls="panel5-content"
                id="panel5-header"
                sx={{
                  backgroundColor: "#ffffff",
                  borderBottom: "1px solid #e0e0e0",
                  borderTopLeftRadius: "5px",
                  borderTopRightRadius: "5px",
                  minHeight: "48px",
                  height: "48px",
                  "@media (max-width: 768px)": {
                    minHeight: "60px",
                    height: "60px",
                  },
                  "& .MuiAccordionSummary-content": {
                    margin: "8px 0",
                  },
                  "& .MuiAccordionSummary-expandIconWrapper": {
                    transform: "rotate(0deg)",
                    transition: "transform 0.2s ease-in-out",
                  },
                  "&.Mui-expanded .MuiAccordionSummary-expandIconWrapper": {
                    transform: "rotate(180deg)",
                  },
                  "&:hover": {
                    backgroundColor: "rgba(12, 15, 36, 0.1)",
                  },
                  "&.Mui-expanded": {
                    backgroundColor: "rgba(12, 15, 36, 0.1)",
                    minHeight: "48px",
                    height: "48px",
                    "@media (max-width: 768px)": {
                      minHeight: "60px",
                      height: "60px",
                    },
                  },
                }}
              >
                <div
                  style={{
                    fontWeight: "600",
                    color: "#0c0f24",
                    fontSize: "16px",
                    fontFamily: "Manrope, sans-serif",
                  }}
                >
                  FINTRAC Documents & Procedures
                </div>
              </AccordionSummary>
              <AccordionDetails className="accordion-content">
                <div>
                  <ol>
                    <li>
                      Please complete all required FINTRAC documents for your
                      transaction in the Fintracker app.
                    </li>

                    <li>
                      Once you have completed your FINTRAC document in
                      Fintracker, copy & paste the FIN# in the comment box of
                      your related SkySlope checklist item. Do not upload PDF
                      files to SkySlope.
                    </li>
                    <li>
                      Mutually Released deals will need FINTRACs completed
                      before funds can be released.
                    </li>
                  </ol>
                  <h2>Fintracker (PC/Mobile)</h2>

                  <ol>
                    <li>
                      All FINTRAC documents are to be completed in the
                      Fintracker app (Mobile) or{" "}
                      <a
                        href="https://www.fintracker.ca"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Fintracker.ca
                      </a>{" "}
                      (PC) .
                    </li>
                    <li>
                      {" "}
                      Create an account at{" "}
                      <a
                        href="https://www.fintracker.ca"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Fintracker.ca
                      </a>{" "}
                      using your @exprealty.com email address.
                    </li>
                    <li>
                      Visit{" "}
                      <a
                        href="https://www.fintracker.ca/tutorials.html"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        fintracker.ca/tutorials
                      </a>{" "}
                      for tutorials on how to use Fintracker.
                    </li>
                  </ol>
                </div>
              </AccordionDetails>
            </Accordion>
          </div>
        </div>
      </div>
    </ThemeProvider>
  );
}
